const getBaseURL = (): string => {
  if (import.meta.env.VITE_API_URL) {
    return import.meta.env.VITE_API_URL;
  }
  return "/api";
};

export interface PresignedUrlResponse {
  uploadUrl: string;
  s3Key: string;
  expiresInSeconds: number;
}

export interface AllowedFormatsResponse {
  allowedContentTypes: string[];
}

export async function requestPresignedUrl(
  orgId: string,
  podcastId: string,
  file: File,
): Promise<PresignedUrlResponse> {
  const res = await fetch(
    `${getBaseURL()}/organizations/${orgId}/podcasts/${podcastId}/episodes/upload-url`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify({
        filename: file.name,
        contentType: file.type,
        fileSizeBytes: file.size,
      }),
    },
  );
  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: "Upload request failed" }));
    throw new Error(err.error || err.message || "Failed to get upload URL");
  }
  return res.json();
}

export async function fetchAllowedFormats(orgId: string, podcastId: string): Promise<string[]> {
  const res = await fetch(
    `${getBaseURL()}/organizations/${orgId}/podcasts/${podcastId}/episodes/upload-formats`,
    { credentials: "include" },
  );
  if (!res.ok) return ["audio/mpeg", "audio/mp4", "audio/x-m4a", "audio/wav", "audio/ogg"];
  const data: AllowedFormatsResponse = await res.json();
  return data.allowedContentTypes;
}

export interface UploadCallbacks {
  onProgress?: (percent: number) => void;
  signal?: AbortSignal;
}

export function uploadToS3(
  url: string,
  file: File,
  { onProgress, signal }: UploadCallbacks = {},
): Promise<void> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open("PUT", url);
    xhr.setRequestHeader("Content-Type", file.type);

    if (signal) {
      signal.addEventListener("abort", () => xhr.abort());
    }

    xhr.upload.addEventListener("progress", (e) => {
      if (e.lengthComputable && onProgress) {
        onProgress(Math.round((e.loaded / e.total) * 100));
      }
    });

    xhr.addEventListener("load", () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        resolve();
      } else {
        reject(new Error(`Upload failed with status ${xhr.status}`));
      }
    });

    xhr.addEventListener("error", () => reject(new Error("Upload failed")));
    xhr.addEventListener("abort", () => reject(new DOMException("Upload cancelled", "AbortError")));

    xhr.send(file);
  });
}
